// @story HIR-HR16 HIR-HR17
// MessagesPage — every message between Hiring and this school's applicants,
// in one place (owner, 29 Oct 2026: "the campaign should be messages … the
// incoming and the outgoing … should be there").
//
// Blocks along the top, each opening its list underneath, as on the Pipeline:
//   Inbox      what applicants sent back: the school's conversations in
//              Messages (Communication's inbox, GET /api/communication/v1/
//              conversations, read with the person's own sign-in) and the
//              replies to Hiring's campaigns (GET /api/campaigns/inbox).
//              The words stay in Messages; each one opens there.
//   Sent       what Hiring handed to Messages (GET /api/dashboard/messages-sent)
//   Campaigns  the school's campaigns, to write and send (the campaign list)
//
// Messages (the Communication app) is where a conversation is read and
// answered; this page lists them and opens each one there.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { Button, Card, EmptyState, Icon, Input, Modal, Radio, StatusPill, Table } from '../../ds';
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from '../../components/page';
import MessagesSent from './MessagesSent';
import { CampaignList } from '../campaigns/CampaignsPage';
import MessagingHoldSwitch from '../settings/MessagingHoldSwitch';
import WhatsAppMoments from './WhatsAppMoments';
import WhatsAppAssistant from './WhatsAppAssistant';

const NEW_KINDS = [
  { value: 'one_time', label: 'One-time', about: 'goes once, when you press Send' },
  { value: 'automatic', label: 'Automatic', about: 'goes by itself when a candidate reaches a stage' },
  { value: 'reminder', label: 'AI reminder', about: 'goes again every few days; the AI sets the rule from your words' },
];

const when = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');
const CHANNEL = { email: 'Email', whatsapp: 'WhatsApp', app: 'App' };

/* The school's Messages app, from Hiring's own address. */
function messagesApp() {
  const h = String(window.location.hostname || '');
  const school = (h.match(/^hiring\.([a-z0-9-]+)\./) || [])[1];
  return school ? `https://communication.${school}.equaos.in` : null;
}

/* Communication's conversations, through the gateway with the person's own
   sign-in (the same call the Messages app makes). Not every member of the
   hiring team may read the school's inbox: then this part says so. */
async function readConversations() {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/communication/v1/conversations?limit=100', { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (res.status === 401 || res.status === 403) return { allowed: false, items: [] };
  if (!res.ok) throw new Error(`Messages answered ${res.status}`);
  const j = await res.json();
  return { allowed: true, items: await withApplicants(j.items || []) };
}

/* Who wrote (owner, 30 Oct 2026): Messages links a reply to the applicant
   Hiring last wrote to (contactRef { source: 'hiring', id }); Hiring adds
   their name and the role they applied for. A conversation Messages could
   not link keeps no name, and says so. */
async function withApplicants(items) {
  const ids = [...new Set(items.filter((c) => c.contactRef?.source === 'hiring' && c.contactRef.id).map((c) => c.contactRef.id))];
  if (!ids.length) return items;
  const people = await api.get('/campaigns/inbox-people', { params: { ids: ids.join(',') } })
    .then((r) => r.data?.people || []).catch(() => []);
  const byId = new Map(people.map((p) => [p.id, p]));
  return items.map((c) => (c.contactRef?.source === 'hiring' && byId.has(c.contactRef.id) ? { ...c, applicant: byId.get(c.contactRef.id) } : c));
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // A new communication (a campaign underneath), from the page's header (owner, 29 Oct 2026:
  // "where is the create campaign?" … "like a new communication": the word is Communication).
  const [creating, setCreating] = useState(null); // null | { name, busy, msg }
  // Templates is no longer a place of its own: each built-in message shows its wording.
  const view = params.get('view') === 'templates' ? 'campaigns' : (params.get('view') || 'inbox');
  const setView = (key) => setParams(key === 'inbox' ? {} : { view: key }, { replace: true });

  const [inbox, setInbox] = useState({ phase: 'loading' });
  const [sent, setSent] = useState(null);
  const [campaigns, setCampaigns] = useState(null);

  const load = useCallback(() => {
    setInbox({ phase: 'loading' });
    Promise.all([
      readConversations().catch((e) => ({ allowed: true, items: [], error: e.message })),
      api.get('/campaigns/inbox').then((r) => r.data).catch(() => ({ total: 0, replies: [] })),
    ]).then(([conv, replies]) => setInbox({ phase: 'ready', conv, replies }));
    api.get('/dashboard/messages-sent', { params: { days: 30 } }).then((r) => setSent(r.data)).catch(() => setSent(null));
    api.get('/campaigns').then((r) => setCampaigns(r.data)).catch(() => setCampaigns(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const conv = inbox.conv?.items || [];
  const waiting = conv.filter((c) => c.unanswered).length;
  const replyTotal = inbox.replies?.total || 0;
  const t = sent?.totals;
  const sentCount = t ? (t.emails || 0) + (t.whatsapps || 0) : null;
  const campaignCount = campaigns?.campaigns ? campaigns.campaigns.length : null;

  /* Name it, and it opens to write: the campaign's own page (who it goes to,
     the email and WhatsApp, a test, then send). Nothing is sent here. */
  const create = () => {
    setCreating((c) => ({ ...c, busy: true, msg: '' }));
    api.post('/campaigns', { name: creating.name.trim(), kind: creating.kind || 'one_time' })
      .then((r) => navigate(`/campaigns/${r.data.campaign.id}`))
      .catch((e) => setCreating((c) => ({ ...c, busy: false, msg: e?.response?.data?.message || 'Could not create it. Try again.' })));
  };

  return (
    <Page>
      <PageHeader
        title="Messages"
        subtitle="Everything between Hiring and this school's applicants: what came in, what went out, and the communications."
        actions={(
          <>
            {messagesApp() && <Button variant="secondary" icon={<Icon name="chat-circle-text" />} onClick={() => window.open(messagesApp(), '_blank', 'noopener')}>Open Messages</Button>}
            <Button variant="ghost" icon={<Icon name="arrow-clockwise" />} onClick={load}>Refresh</Button>
            <Button icon={<Icon name="plus" />} onClick={() => setCreating({ name: '', kind: 'one_time', busy: false, msg: '' })}>New communication</Button>
          </>
        )}
      />

      <StatRow>
        <Stat label="Inbox" value={inbox.phase === 'ready' ? conv.length + replyTotal : '…'}
          hint={waiting ? `${waiting} waiting for an answer` : 'conversations and replies'}
          tone={waiting ? 'warning' : undefined} active={view === 'inbox'} onClick={() => setView('inbox')} />
        <Stat label="Sent" value={sentCount === null ? '…' : sentCount}
          hint={t ? `${t.emails} emails · ${t.whatsapps} WhatsApps, last 30 days` : 'last 30 days'}
          active={view === 'sent'} onClick={() => setView('sent')} />
        <Stat label="Not delivered" value={t ? t.whatsappsNotDelivered || 0 : '…'} hint="WhatsApps that did not arrive"
          tone={t && t.whatsappsNotDelivered ? 'danger' : undefined} onClick={() => setView('sent')} />
        <Stat label="Communications" value={campaignCount === null ? '…' : campaignCount} hint="write and send to applicants"
          active={view === 'campaigns'} onClick={() => setView('campaigns')} />
      </StatRow>

      {view === 'inbox' && <Inbox state={inbox} />}
      {view === 'sent' && <MessagesSent embedded />}
      {view === 'campaigns' && (
        <>
          {/* The pause on messages after application, for every message at once.
              Each built-in message's own wording is on its page (owner, 30 Oct 2026). */}
          <Section title="Messages after application" subtitle="paused or going, for every message at once">
            <div className="dps-legacy"><MessagingHoldSwitch /></div>
          </Section>
          {/* HIR-HR20: WhatsApp only where the school has switched it on, moment by moment. */}
          <Section title="WhatsApp to candidates" subtitle="moment by moment · off means email only">
            <WhatsAppMoments />
          </Section>
          {/* HIR-CN5 on WhatsApp: Hiring's own bot answers candidates who write in. */}
          <Section title="WhatsApp assistant" subtitle="replies to candidates as HR · off by default">
            <WhatsAppAssistant />
          </Section>
          <CampaignList embedded />
        </>
      )}

      <Modal open={Boolean(creating)} title="New communication" onClose={() => setCreating(null)} width="520px"
        footer={creating && (
          <>
            <Button variant="ghost" onClick={() => setCreating(null)} disabled={creating.busy}>Cancel</Button>
            <Button onClick={create} disabled={creating.busy || !creating.name.trim()} loading={creating.busy}>Create and write it</Button>
          </>
        )}>
        {creating && (
          // A form, so Enter in the name creates it too.
          <form style={{ display: 'grid', gap: 12 }} onSubmit={(e) => { e.preventDefault(); if (creating.name.trim() && !creating.busy) create(); }}>
            <p style={{ margin: 0, color: 'var(--text-2)', fontSize: 'var(--fs-body-sm)' }}>
              Give it a name your team will recognise. Next you choose who it goes to and write the email and WhatsApp; nothing is sent until you send it.
            </p>
            {/* Which kind (owner, 29 Oct 2026); it can be changed on the next page until it goes out. */}
            <div style={{ display: 'grid', gap: 8 }}>
              {NEW_KINDS.map((k) => (
                <Radio key={k.value} name="new-campaign-kind" label={`${k.label} — ${k.about}`} checked={creating.kind === k.value}
                  onChange={() => setCreating((c) => ({ ...c, kind: k.value }))} />
              ))}
            </div>
            <Input id="new-campaign-name" label="Name of the communication" required value={creating.name} placeholder="e.g. Round 1 dates — November"
              onChange={(e) => setCreating((c) => ({ ...c, name: e.target.value }))} />
            {creating.msg && <Note tone="danger">{creating.msg}</Note>}
          </form>
        )}
      </Modal>
    </Page>
  );
}

function Inbox({ state }) {
  if (state.phase === 'loading') return <Card><Loading label="Reading the inbox" /></Card>;
  const { conv, replies } = state;
  const app = messagesApp();
  const open = (path) => app && window.open(`${app}${path}`, '_blank', 'noopener');

  const convColumns = [
    { key: 'who', label: 'From', render: (c) => (c.applicant
      ? <span style={{ fontWeight: 500 }}>{c.applicant.name}</span>
      : <span style={{ color: 'var(--text-muted)' }}>Not linked to an applicant</span>) },
    { key: 'role', label: 'Applied for', render: (c) => c.applicant?.role || '—' },
    { key: 'channel', label: 'Channel', render: (c) => CHANNEL[c.lastChannel] || c.lastChannel || '—' },
    { key: 'last', label: 'Last message in', render: (c) => when(c.lastInboundAt) },
    { key: 'answered', label: 'Last answer', render: (c) => when(c.lastOutboundAt) },
    { key: 'state', label: 'Status', render: (c) => (c.unanswered
      ? <StatusPill tone="warning">Waiting for an answer</StatusPill>
      : c.state === 'closed' ? <StatusPill tone="neutral">Closed</StatusPill> : <StatusPill tone="success">Answered</StatusPill>) },
    { key: 'open', label: '', render: (c) => (app ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); open(`/inbox/${c.id}`); }}>Open in Messages</Button> : null) },
  ];
  const replyColumns = [
    { key: 'name', label: 'Applicant', render: (r) => <span style={{ fontWeight: 500 }}>{r.name}</span> },
    { key: 'campaign', label: 'Replied to', render: (r) => r.campaign },
    { key: 'channel', label: 'Channel', render: (r) => CHANNEL[r.channel] || r.channel },
    { key: 'choice', label: 'Answer', render: (r) => (r.choice ? <StatusPill tone="info" dot={false}>{r.choice}</StatusPill> : <span style={{ color: 'var(--text-muted)' }}>in Messages</span>) },
    { key: 'at', label: 'When', render: (r) => when(r.at) },
    { key: 'open', label: '', render: (r) => (r.link ? <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); window.open(r.link, '_blank', 'noopener'); }}>Open in Messages</Button> : null) },
  ];

  return (
    <>
      {conv.error && <Note tone="warning">The school's Messages inbox could not be read just now ({conv.error}).</Note>}
      {!conv.allowed && <Note tone="info">Your sign-in cannot read the school's Messages inbox. Ask your administrator for Messages access, or open Messages.</Note>}

      <Section title="Conversations" subtitle="What applicants and others sent the school, from Messages · the words are read and answered there" padding="20px 0 8px">
        {conv.items.length
          ? <Table columns={convColumns} rows={conv.items} getRowId={(c) => c.id} onRowClick={(c) => open(`/inbox/${c.id}`)} />
          : <EmptyState icon={<Icon name="chat-circle-text" />} title="No conversations yet" description="When someone writes to the school by WhatsApp or email, it appears here." />}
      </Section>

      <Section title="Replies to communications" subtitle={`${replies.total || 0} repl${replies.total === 1 ? 'y' : 'ies'}${replies.isPartial ? ` · newest ${replies.shown} shown` : ''}`} padding="20px 0 8px">
        {replies.replies?.length
          ? <Table columns={replyColumns} rows={replies.replies} getRowId={(r) => r.id} onRowClick={(r) => r.link && window.open(r.link, '_blank', 'noopener')} />
          : <EmptyState icon={<Icon name="paper-plane" />} title="No replies yet" description="Replies to a communication's email or WhatsApp appear here." />}
      </Section>
    </>
  );
}
